import { Box, Button, Paper, Alert, TextField, Stack, Typography } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { Feature } from '@/types/mapbox';

import { useInitiateMeetTerri } from '@/api/endpoints/meet-terri/useInitiateMeetTerri';
import { useVendorSearchStream } from '@/api/endpoints/meet-terri/useVendorSearchStream';
// import { useGetSearchHistory } from '@/api/endpoints/vendor/useGetSearchHistory';
import { useSearchVendors } from '@/api/endpoints/vendor/useSearchVendors';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import { PublicRoutes, PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useIPLocationLazy } from '@/hooks/useIPLocation';
import { useTypingAnimation } from '@/hooks/useTypingAnimation';
import { convertIPLocationToMapboxFeature } from '@/utils/locationToMapboxFeature';

import MeetTerriDialog from '@/components/dialogs/MeetTerriDialog';
import MapboxAddressAutocomplete from '@/components/form/MapboxAddressAutocomplete';
import BaseLottie from '@/components/lottie/BaseLottie';
// import RecentSearchCard from '@/components/vendor/RecentSearchCard';

import vendorSearchAnimation from '../../../../public/assets/images/vendor_search.json';

const VendorSearchSection: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const domainConfig = useDomainConfig();
  const { selectedPropertyLocation } = usePropertyManager();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState<Feature | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showMeetTerriDialog, setShowMeetTerriDialog] = useState(false);
  const [meetTerriError, setMeetTerriError] = useState<string | null>(null);
  const [meetTerriSessionToken, setMeetTerriSessionToken] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchVendorsMutation = useSearchVendors();
  const initiateMeetTerriMutation = useInitiateMeetTerri();
  const { lastSearchRequest, clearLastRequest } = useVendorSearchStream(meetTerriSessionToken);
  const isFixdDomain = domainConfig.type === 'fixdai';

  // Typing animation for placeholder
  const placeholderExamples = [
    'Fix leaking faucet in master bathroom',
    'Install ceiling fan in living room',
    'Finish 1300 sq ft basement',
    'Repair broken garage door opener',
    'Paint exterior of two-story house',
    'Replace old HVAC system',
    'Install new kitchen countertops',
  ];

  const typingPlaceholder = useTypingAnimation({
    texts: placeholderExamples,
    typingSpeed: 50,
    deletingSpeed: 30,
    delayBetweenTexts: 2000,
  });

  // Get search history for logged-in users - DISABLED
  // const { data: searchHistory } = useGetSearchHistory(!!user);
  // const mostRecentSearch = searchHistory?.[0]; // API returns sorted by most recent

  // IP-based location detection for non-logged-in users
  const ipLocation = useIPLocationLazy({
    onSuccess: data => {
      // Only use IP location if user is not logged in and no address is set
      if (!user && !selectedAddress && !hasInitialized) {
        const feature = convertIPLocationToMapboxFeature(data, 'ip-location');
        setSelectedAddress(feature);
        setHasInitialized(true);
      }
    },
  });

  // Trigger IP location detection for non-logged-in users
  useEffect(() => {
    if (!user && !hasInitialized && !selectedAddress && !ipLocation.isLoading && !ipLocation.data) {
      ipLocation.refetch();
    }
  }, [user, hasInitialized, selectedAddress, ipLocation]);

  // Prepopulate address with selected property location if user is logged in
  useEffect(() => {
    if (user && selectedPropertyLocation && !hasInitialized && !selectedAddress) {
      // Create a simplified Feature object from the property location
      const propertyAddress = selectedPropertyLocation.address_text || '';
      if (propertyAddress) {
        // Create a minimal Feature object that will work with the component
        const feature: Feature = {
          type: 'Feature',
          id: 'property-location',
          geometry: {
            type: 'Point',
            coordinates: [0, 0], // Default coordinates as we don't have them
          },
          properties: {
            mapbox_id: 'property-location',
            full_address: propertyAddress,
            place_formatted: propertyAddress,
            name: propertyAddress,
            name_preferred: propertyAddress,
            feature_type: 'address',
            coordinates: {
              accuracy: 'point',
              latitude: 0,
              longitude: 0,
              routable_points: [],
            },
            context: {
              address: { mapbox_id: '', name: '', address: '', street_name: '' },
              country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
              district: { mapbox_id: '', name: '' },
              place: { mapbox_id: '', name: '' },
              postcode: { mapbox_id: '', name: '' },
              region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
              street: { mapbox_id: '', name: '' },
            },
            match_code: {
              address_number: '',
              confidence: '',
              country: '',
              locality: '',
              place: '',
              postcode: '',
              region: '',
              street: '',
            },
          },
        };
        setSelectedAddress(feature);
        setHasInitialized(true);
      }
    }
  }, [user, selectedPropertyLocation, hasInitialized, selectedAddress]);

  const handleSearch = useCallback(async () => {
    // Clear any previous errors
    setErrorMessage(null);

    // Trim whitespace from search query
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery || !selectedAddress) {
      setErrorMessage('Please enter both a search query and an address');
      return;
    }

    // Prevent double-tap on mobile
    if (isSearching || searchVendorsMutation.isPending) {
      return;
    }

    setIsSearching(true);

    try {
      // Use full_address if available, otherwise fall back to place_formatted or name
      const addressToUse =
        selectedAddress.properties.full_address ||
        selectedAddress.properties.place_formatted ||
        selectedAddress.properties.name ||
        '';

      if (!addressToUse) {
        setErrorMessage('Please select a valid address from the dropdown');
        setIsSearching(false);
        return;
      }

      const result = await searchVendorsMutation.mutateAsync({
        address: addressToUse,
        query: trimmedQuery,
      });

      // Check if we actually got results
      if (!result) {
        throw new Error('No response from server');
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsSearching(false);
        const targetRoute = user ? PMPRoutes.vendorSearchResults : PublicRoutes.vendorSearchResults;
        history.replace(targetRoute, {
          vendors: result.vendors || [],
          searchQuery: trimmedQuery,
          address: addressToUse,
          disclaimer: result.disclaimer,
          search_query: result.search_query,
          searchId: result.searchId,
          diy_video: result.diy_video,
          cost_estimate: result.cost_estimate,
          permit_requirement: result.permit_requirement,
          project_readiness: result.project_readiness,
          vendor_questions: result.vendor_questions,
        });
      }, 1500);
    } catch (error) {
      setIsSearching(false);

      // Set a more detailed error message
      if (error instanceof Error) {
        if (error.message.includes('Network') || error.message.includes('fetch')) {
          setErrorMessage('Network error. Please check your internet connection and try again.');
        } else {
          const msg =
            error.message || 'An error occurred while searching for providers. Please try again.';
          setErrorMessage(msg);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    }
  }, [searchQuery, selectedAddress, searchVendorsMutation, user, history, isSearching]);

  // Handle incoming vendor search requests from Meet Terri calls
  useEffect(() => {
    if (lastSearchRequest && !isSearching) {
      // Set the search query from the Meet Terri call
      setSearchQuery(lastSearchRequest.search_query);

      // Create a Feature object for the address
      const addressFeature: Feature = {
        type: 'Feature',
        id: 'meet-terri-address',
        geometry: {
          type: 'Point',
          coordinates: [0, 0], // Will be resolved by the search
        },
        properties: {
          mapbox_id: 'meet-terri-address',
          full_address: lastSearchRequest.address,
          place_formatted: lastSearchRequest.address,
          name: lastSearchRequest.address,
          name_preferred: lastSearchRequest.address,
          feature_type: 'address',
          coordinates: {
            accuracy: 'point',
            latitude: 0,
            longitude: 0,
            routable_points: [],
          },
          context: {
            address: { mapbox_id: '', name: '', address: '', street_name: '' },
            country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
            district: { mapbox_id: '', name: '' },
            place: { mapbox_id: '', name: '' },
            postcode: { mapbox_id: '', name: '' },
            region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
            street: { mapbox_id: '', name: '' },
          },
          match_code: {
            address_number: '',
            confidence: '',
            country: '',
            locality: '',
            place: '',
            postcode: '',
            region: '',
            street: '',
          },
        },
      };

      setSelectedAddress(addressFeature);

      // Clear the request to prevent re-triggering
      clearLastRequest();

      // Automatically trigger the search after a short delay
      setTimeout(() => {
        handleSearch();
      }, 1000);
    }
  }, [lastSearchRequest, isSearching, clearLastRequest, handleSearch]);

  // Clean up timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    []
  );

  // const handleMeetTerriClick = () => {
  //   setShowMeetTerriDialog(true);
  //   setMeetTerriError(null);
  // };

  const handleMeetTerriCall = async (phoneNumber: string) => {
    try {
      const result = await initiateMeetTerriMutation.mutateAsync({
        phone_number: phoneNumber,
      });

      if (result.success) {
        // Store session token if provided (for anonymous users)
        if (result.session_token) {
          setMeetTerriSessionToken(result.session_token);
        }
        // Close dialog on success
        setShowMeetTerriDialog(false);
        setMeetTerriError(null);
        // Show success message
        setErrorMessage(null);
      } else {
        setMeetTerriError(result.message || 'Failed to initiate call. Please try again.');
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'An error occurred while initiating the call. Please try again.';
      setMeetTerriError(errorMsg);
    }
  };

  // const handleLoadRecentSearch = useCallback((query: string, address: string) => {
  //   // Set the search query
  //   setSearchQuery(query);

  //   // Create a Feature object for the address
  //   const addressFeature: Feature = {
  //     type: 'Feature',
  //     id: 'recent-search-address',
  //     geometry: {
  //       type: 'Point',
  //       coordinates: [0, 0], // Will be resolved by the search
  //     },
  //     properties: {
  //       mapbox_id: 'recent-search-address',
  //       full_address: address,
  //       place_formatted: address,
  //       name: address,
  //       name_preferred: address,
  //       feature_type: 'address',
  //       coordinates: {
  //         accuracy: 'point',
  //         latitude: 0,
  //         longitude: 0,
  //         routable_points: [],
  //       },
  //       context: {
  //         address: { mapbox_id: '', name: '', address: '', street_name: '' },
  //         country: { mapbox_id: '', name: '', country_code: '', country_code_alpha_3: '' },
  //         district: { mapbox_id: '', name: '' },
  //         place: { mapbox_id: '', name: '' },
  //         postcode: { mapbox_id: '', name: '' },
  //         region: { mapbox_id: '', name: '', region_code: '', region_code_full: '' },
  //         street: { mapbox_id: '', name: '' },
  //       },
  //       match_code: {
  //         address_number: '',
  //         confidence: '',
  //         country: '',
  //         locality: '',
  //         place: '',
  //         postcode: '',
  //         region: '',
  //         street: '',
  //       },
  //     },
  //   };

  //   setSelectedAddress(addressFeature);
  // }, []);

  return (
    <>
      {/* Search Form - Always Visible */}
      <Box sx={{ width: '100%', maxWidth: '800px' }}>
        {!isSearching ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 3 },
              borderRadius: 3,
              width: '100%',
              maxWidth: '800px',
            }}
          >
            <Stack spacing={3}>
              <TextField
                fullWidth
                placeholder={typingPlaceholder || ''}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  // Allow search on Enter key press
                  if (e.key === 'Enter' && searchQuery.trim() && selectedAddress) {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                variant="outlined"
                disabled={isSearching || searchVendorsMutation.isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <SearchLgIcon
                        width={20}
                        height={20}
                        style={{ marginRight: 8, color: '#666' }}
                      />
                    ),
                  },
                  htmlInput: {
                    'aria-label': 'Search for providers',
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'off',
                    spellCheck: false,
                    fontWeight: 600,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontWeight: 600,
                    backgroundColor: '##fff',
                    '&:hover': {
                      backgroundColor: '#F3F4F6',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              />
              <MapboxAddressAutocomplete
                value={selectedAddress}
                onChange={setSelectedAddress}
                placeholder={
                  !user && ipLocation.isLoading
                    ? 'Detecting your location...'
                    : 'Enter your address to find providers near you'
                }
                disabled={isSearching || searchVendorsMutation.isPending}
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSearch}
                disabled={
                  !searchQuery.trim() ||
                  !selectedAddress ||
                  isSearching ||
                  searchVendorsMutation.isPending
                }
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E5E7EB',
                    opacity: 0.5,
                    color: '#fff',
                  },
                }}
              >
                Find Pros for My Project
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              width: '100%',
              maxWidth: '800px',
              background: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {isFixdDomain ? (
              <Box sx={{ width: 200, height: 200 }}>
                <img
                  src="/assets/brands/fixdai/search_loader.svg"
                  alt="Searching for contractors"
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
            ) : (
              <Box sx={{ width: 200, height: 200 }}>
                <BaseLottie animationData={vendorSearchAnimation} loop autoplay />
              </Box>
            )}
            <Stack spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isFixdDomain
                  ? 'Finding the best contractors for your project...'
                  : 'Deploying our AI agent to scout the perfect providers...'}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {isFixdDomain
                  ? 'Analyzing local contractors and preparing cost estimates for you.'
                  : 'Our digital detective is on the case! Sit tight while we match you with top providers tailored to your needs.'}
              </Typography>
            </Stack>
          </Paper>
        )}

        {!isSearching && (searchVendorsMutation.isError || errorMessage) && (
          <Alert
            severity="error"
            sx={{ maxWidth: '800px', width: '100%' }}
            onClose={() => {
              setErrorMessage(null);
              searchVendorsMutation.reset();
            }}
          >
            {errorMessage || 'An error occurred while searching for providers. Please try again.'}
          </Alert>
        )}
      </Box>

      {/* Recent Search Card for logged-in users - DISABLED */}
      {/* {user && mostRecentSearch && !isSearching && (
        <Box sx={{ width: '100%', maxWidth: '800px' }}>
          <RecentSearchCard
            searchHistory={mostRecentSearch}
            onLoadSearch={handleLoadRecentSearch}
          />
        </Box>
      )} */}

      {/* Meet Terri Dialog */}
      <MeetTerriDialog
        open={showMeetTerriDialog}
        onClose={() => {
          setShowMeetTerriDialog(false);
          setMeetTerriError(null);
        }}
        onCall={handleMeetTerriCall}
        isLoading={initiateMeetTerriMutation.isPending}
        error={meetTerriError}
      />
    </>
  );
};

export default VendorSearchSection;
